"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import { LogoMark, LogoName } from "@/components/ui/logo";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { GetEarlyAccessButton } from "@/components/ui/buttons";
import { useEarlyAccess } from "@/components/early-access";
import { cn } from "@/lib/utils";
import { DOCS_URL } from "@/lib/docs/url";

/**
 * Documentation lives on its own host and opens in a new tab, like every
 * off-site link. Re-exported here so the existing
 * `import { DOCS_URL } from "@/components/navbar"` call sites keep working;
 * the constant itself lives in `lib/docs/url.ts`, which the dashboard can
 * import without pulling in the marketing navbar.
 */
export { DOCS_URL };

type NavLink = { kind: "link"; label: string; href: string; aria?: string; external?: boolean };
/**
 * A dropdown. With `href` it is also a page: the label is a link to it and a
 * separate chevron button opens the menu (Media, since 2026-09-11). Without
 * `href` the label itself is the toggle, as Resources has always been.
 */
type NavMenu = {
  kind: "menu";
  label: string;
  href?: string;
  /**
   * The menu's own page, listed first in its panel (2026-10-21). Hover opens
   * the panel the moment the pointer reaches the label, so nothing showed that
   * the label was itself a page: visitors read "Developers" as a heading over
   * two links. The first row now says where the label goes.
   */
  overview?: { label: string; hint: string };
  items: Array<{ label: string; href: string; hint: string }>;
};
type NavEntry = NavLink | NavMenu;

/**
 * Root-relative rather than bare hashes: the navbar is shared with every
 * marketing route, and a bare `#models` on `/engineers` scrolls to nothing.
 * `/#models` goes home and lands on the section from any page.
 *
 * Six entries since 2026-10-13 (seven before), so the row fits at `xl` with
 * room to spare; the drawer still takes over below it. History of the order
 * is in `kb/decisions.md`.
 */
/**
 * Since 2026-10-13: by audience, from the team's page-content document
 * ("Developers | Enterprise | PK Endpoint | About") and the growth audit,
 * with every page the old row linked still one click or one menu away:
 *
 *   Developers ▾   Models, Pricing          what a builder needs
 *   Enterprise ▾   Dedicated, Hire an FDE   what a buyer needs
 *   PK Endpoint                             the sovereign story, on its own
 *   Blog                                    top level, as the audit asked
 *   About ▾        News, Press, Press kit, Careers
 *   Docs                                    last, opens a new tab
 *
 * The three menus carry an `href`, so the label opens the audience page and
 * the chevron opens the menu, the pattern Media introduced. Careers has a home
 * again under About (it lost its navbar place when Resources went).
 */
const LINKS: NavEntry[] = [
  {
    kind: "menu",
    label: "Developers",
    href: "/developers",
    overview: { label: "Developers overview", hint: "One OpenAI-compatible API, priced in rupees" },
    items: [
      { label: "Models", href: "/models", hint: "Every route, with rupee prices" },
      { label: "Pricing", href: "/pricing", hint: "Pay per token, and what a month costs" },
    ],
  },
  {
    kind: "menu",
    label: "Enterprise",
    href: "/enterprise",
    overview: { label: "Enterprise overview", hint: "In-Pakistan processing, governance and PKR billing" },
    items: [
      { label: "Dedicated inference", href: "/dedicated", hint: "Ascend NPUs reserved for you" },
      { label: "Hire an FDE", href: "/engineers", hint: "Engineers who ship it with your team" },
    ],
  },
  { kind: "link", label: "PK Endpoint", href: "/pk-endpoint" },
  { kind: "link", label: "Blog", href: "/blog" },
  {
    kind: "menu",
    label: "About",
    href: "/about",
    overview: { label: "About Paranine", hint: "The mission, the roadmap, the founder and partners" },
    items: [
      { label: "News & Announcements", href: "/media/news", hint: "Official announcements & updates from paranine" },
      { label: "Press", href: "/media/press-releases", hint: "External coverage, and stories featuring paranine" },
      { label: "Press kit", href: "/media", hint: "Logos, brand assets and boilerplate" },
      { label: "Careers", href: "/careers", hint: "Work with us" },
    ],
  },
  { kind: "link", label: "Docs", href: DOCS_URL, external: true },
];

const DESKTOP_LINK =
  "group relative font-sans text-sm text-foreground/80 transition-colors hover:text-foreground";

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn("transition-transform duration-200", open && "rotate-180")}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/** Remix Icon `arrow-right-line` (Apache-2.0), inlined rather than adding the package for one glyph. */
function ArrowRight({ className }: { className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" />
    </svg>
  );
}

function Underline() {
  return (
    <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-foreground transition-all duration-300 group-hover:w-full" />
  );
}

/**
 * The Resources dropdown. Opens on hover and on click, closes on leave,
 * Escape, or a click elsewhere; the trigger is a real button with
 * `aria-expanded`, so keyboard users get the same menu pointer users do.
 */
function DesktopMenu({ menu }: { menu: NavMenu }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  // A short grace period so the pointer can cross the gap to the panel.
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 140);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      {menu.href ? (
        /* A menu that is also a page (Media). Two controls, because one
           cannot honestly be both: the label is a link, so a click goes to
           the page as the team asked, and the chevron is a button, so a
           keyboard or touch user who cannot hover can still open the menu.
           Hover opens it for everyone else, from the wrapper above. The
           chevron is a 24px target (WCAG 2.5.8) around a 12px icon, so it
           carries 6px of empty padding on each side. `-ml-1` pulls the left
           in to within 2px of Resources' label-to-chevron gap, and `-mr-1.5`
           cancels the right entirely: without it the space after "Media"
           read visibly wider than between every other pair of entries. */
        <span className="inline-flex items-center gap-1">
          <Link href={menu.href} onClick={() => setOpen(false)} className={DESKTOP_LINK}>
            {menu.label}
            <Underline />
          </Link>
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={`More in ${menu.label}`}
            onClick={() => setOpen((v) => !v)}
            className="-ml-1 -mr-1.5 grid h-6 w-6 place-items-center text-foreground/80 transition-colors hover:text-foreground"
          >
            <Chevron open={open} />
          </button>
        </span>
      ) : (
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className={cn(DESKTOP_LINK, "inline-flex items-center gap-1")}
        >
          {menu.label}
          <Chevron open={open} />
          <Underline />
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            /* Sized to its content, not to a fixed 240px. The desktop nav sets
   `whitespace-nowrap` on the row, which every child inherits, so a
   hint longer than the old `w-60` ran straight out of the panel and
   over the page (caught in dark mode, 2026-09-24, on "Official
   announcements & updates from paranine"). `w-max` lets the panel
   take the width of its longest line, the max keeps it sane, and the
   hint below wraps rather than escaping. */
            className="absolute left-1/2 top-[calc(100%+0.9rem)] w-max min-w-60 max-w-[min(22rem,calc(100vw-2.5rem))] -translate-x-1/2 rounded-xl border border-border bg-background/95 p-1.5 shadow-xl backdrop-blur-xl"
          >
            {menu.href && menu.overview && (
              <>
                <Link
                  href={menu.href}
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  className="group/overview block rounded-lg px-3 py-2 transition-colors hover:bg-foreground/[0.05]"
                >
                  <span className="flex items-center gap-1.5 font-sans text-sm font-medium text-foreground">
                    {menu.overview.label}
                    <ArrowRight className="transition-transform duration-200 group-hover/overview:translate-x-0.5" />
                  </span>
                  <span className="block whitespace-normal font-sans text-xs leading-snug text-muted">
                    {menu.overview.hint}
                  </span>
                </Link>
                <div role="separator" className="mx-3 my-1.5 h-px bg-border" />
              </>
            )}
            {menu.items.map((it) => (
              <Link
                key={it.href}
                href={it.href}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="block rounded-lg px-3 py-2 transition-colors hover:bg-foreground/[0.05]"
              >
                <span className="block font-sans text-sm text-foreground">{it.label}</span>
                <span className="block whitespace-normal font-sans text-xs leading-snug text-muted">{it.hint}</span>
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DesktopLink({ link }: { link: NavLink }) {
  if (link.external) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={link.aria}
        className={DESKTOP_LINK}
      >
        {link.label}
        <Underline />
      </a>
    );
  }
  return (
    <a href={link.href} aria-label={link.aria} className={DESKTOP_LINK}>
      {link.label}
      <Underline />
    </a>
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { open: openEarlyAccess } = useEarlyAccess();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50 flex justify-center"
    >
      {/*
        Layout, and why it is a grid on desktop.
        The links used to be absolutely centred (`left-1/2 -translate-x-1/2`)
        over a flex row, which meant nothing stopped them from running into the
        logo or the CTA. Three grid tracks: `minmax(0,1fr)` on each side splits
        the leftover space equally, so the middle track stays perfectly
        centred *and* the sides always have room at normal metrics. With seven
        entries the desktop row starts at `xl`; below that the drawer takes
        over. (Not a hard guarantee under text-only zoom, where the side tracks
        can be pushed past their share; page zoom drops below `xl` first and
        gets the drawer instead.)
      */}
      <div
        className={cn(
          "relative flex items-center justify-between transition-all duration-500",
          "xl:grid xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]",
          "ease-[cubic-bezier(0.22,1,0.36,1)] rounded-2xl border border-border backdrop-blur-xl",
          scrolled
            ? "mt-3 h-14 w-[calc(100%-1.25rem)] max-w-6xl bg-background/80 px-4 shadow-[0_10px_40px_-12px_rgb(var(--shadow-color)/0.18)] sm:px-5"
            : "mt-4 h-16 w-[calc(100%-1.5rem)] max-w-[82rem] bg-background/45 px-5 sm:px-6",
        )}
      >
        <a
          href="/"
          aria-label="paranine"
          className="flex shrink-0 items-center text-foreground"
        >
          <LogoMark className="h-7 w-6 shrink-0" />
          <motion.span
            animate={{
              opacity: scrolled ? 0 : 1,
              maxWidth: scrolled ? 0 : 124,
              marginLeft: scrolled ? 0 : 10,
            }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden whitespace-nowrap"
          >
            <LogoName className="h-7 w-[6.25rem]" />
          </motion.span>
        </a>

        {/* desktop nav: centre grid track, so it is centred and can never
            collide with the logo or the CTA */}
        <nav className="hidden items-center justify-center gap-6 whitespace-nowrap xl:flex 2xl:gap-8">
          {LINKS.map((entry) =>
            entry.kind === "menu" ? (
              <DesktopMenu key={entry.label} menu={entry} />
            ) : (
              <DesktopLink key={entry.label} link={entry} />
            ),
          )}
        </nav>

        {/* desktop actions */}
        <div className="hidden items-center justify-end gap-3 xl:flex">
          <ThemeToggle />
          <GetEarlyAccessButton withArrow={false} className="whitespace-nowrap px-4 py-2 text-sm" />
        </div>

        {/* mobile + tablet actions */}
        <div className="flex items-center gap-1.5 xl:hidden">
          <ThemeToggle />
          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="grid h-10 w-10 place-items-center text-foreground"
          >
            <div className="relative h-3.5 w-4.5">
              <motion.span
                className="absolute left-0 top-0 block h-[1.5px] w-full bg-current"
                animate={menuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.25 }}
              />
              <motion.span
                className="absolute left-0 top-[6px] block h-[1.5px] w-full bg-current"
                animate={menuOpen ? { opacity: 0 } : { opacity: 1 }}
                transition={{ duration: 0.2 }}
              />
              <motion.span
                className="absolute left-0 top-[12px] block h-[1.5px] w-full bg-current"
                animate={menuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.25 }}
              />
            </div>
          </button>
        </div>

        {/* drawer: `xl:hidden`, matching its trigger. These two breakpoints
            must move together: when the trigger and the panel disagreed, a
            tablet got a hamburger that locked body scroll over a panel that
            was `display:none`, with the desktop nav also hidden. */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-x-0 top-[calc(100%+0.5rem)] max-h-[calc(100vh-6rem)] origin-top overflow-y-auto rounded-2xl border border-border bg-background/95 p-4 shadow-xl backdrop-blur-xl xl:hidden"
            >
              <nav className="flex flex-col">
                {LINKS.map((entry) =>
                  entry.kind === "menu" && entry.href ? (
                    /* A menu that is also a page: the page first, as a link
                       like any other entry, its sub-pages indented under it.
                       There is no hover in a drawer, so nothing is hidden. */
                    <div key={entry.label} className="border-b border-border/50 py-3.5 last:border-b-0">
                      <Link href={entry.href} onClick={() => setMenuOpen(false)} className="font-serif text-lg">
                        {entry.label}
                      </Link>
                      <div className="mt-2 flex flex-col gap-2 border-l border-border pl-4">
                        {entry.items.map((it) => (
                          <Link
                            key={it.href}
                            href={it.href}
                            onClick={() => setMenuOpen(false)}
                            className="font-serif text-base text-foreground/80"
                          >
                            {it.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : entry.kind === "menu" ? (
                    <div key={entry.label} className="border-b border-border/50 py-3.5 last:border-b-0">
                      <p className="font-sans text-[0.68rem] uppercase tracking-[0.18em] text-muted-2">
                        {entry.label}
                      </p>
                      <div className="mt-2 flex flex-col gap-2">
                        {entry.items.map((it) => (
                          <Link
                            key={it.href}
                            href={it.href}
                            onClick={() => setMenuOpen(false)}
                            className="font-serif text-lg"
                          >
                            {it.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <a
                      key={entry.label}
                      href={entry.href}
                      aria-label={entry.aria}
                      target={entry.external ? "_blank" : undefined}
                      rel={entry.external ? "noopener noreferrer" : undefined}
                      onClick={() => setMenuOpen(false)}
                      className="border-b border-border/50 py-3.5 font-serif text-lg last:border-b-0"
                    >
                      {entry.label}
                    </a>
                  ),
                )}
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    openEarlyAccess();
                  }}
                  className="mt-4 rounded-lg bg-foreground py-3 font-sans text-sm font-medium text-background"
                >
                  Get early access
                </button>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
